/**
 * シーングリッド(PIX Script Studio 風)用の型定義。
 * .pix の AST を「時間軸の行」に変換する。
 *
 * - 1 行 = 1 メッセージ / 1 ナレーション / 1 選択肢 / 1 ジャンプ などの「演出単位」
 * - 各行は累積した表示状態(BGM/BG/立ち絵 5 位置)+ そのターンの変化点情報を持つ
 */

export type CharaPosition = "left-edge" | "left" | "center" | "right" | "right-edge";

export const CHARA_POSITIONS: CharaPosition[] = [
  "left-edge",
  "left",
  "center",
  "right",
  "right-edge",
];

export const CHARA_POSITION_LABEL: Record<CharaPosition, string> = {
  "left-edge": "左端",
  left: "左",
  center: "中",
  right: "右",
  "right-edge": "右端",
};

export interface CharaCell {
  id: string;
  expression: string;
}

export interface BgmCell {
  file: string;
  vol?: number;
}

export interface BgCell {
  name: string;
  fade?: number;
}

export interface SeCell {
  /** play: 効果音再生。stop: 指定スロットの停止。 */
  kind: "play" | "stop";
  /** kind=play の時のみ。manifest.audio.se の ID */
  file?: string;
  /** スロット(0..15)。未指定は 0 */
  slot?: number;
  /** 音量 0..100(percent)。未指定はデフォルト 100 */
  vol?: number;
  /** true でループ再生(Engine は loop !== 0 でループ) */
  loop?: boolean;
  /** この SE を発行している @se 行のソース行番号(1-based)。編集時に行置換に使う */
  sourceLine?: number;
}

export interface MessageCell {
  speaker: string | null;
  /** インラインタグを除いた平文(プレビュー用) */
  plainText: string;
}

export type StateMarker =
  | { kind: "label"; name: string } // ## label
  | { kind: "jump"; target: string } // @jump target
  | { kind: "call"; target: string }
  | { kind: "return" }
  | { kind: "end" }
  | { kind: "choice" }
  | { kind: "macro"; name: string }
  | { kind: "wait"; ms?: number }
  | { kind: "transition"; effect: string; ms?: number }
  | { kind: "effect"; name: string; args: Record<string, unknown> }
  | { kind: "screen"; effect: string; args: Record<string, unknown> };

export interface SceneRow {
  /** React キー */
  id: string;
  /** 1-based 行番号 */
  index: number;
  /** ソース内の行番号(ジャンプ用) */
  sourceLine: number;

  // 状態(累積)
  bgm: BgmCell | null;
  se: SeCell | null; // ワンショット — 表示されるのはトリガーされた行のみ
  bg: BgCell | null;
  chara: Record<CharaPosition, CharaCell | null>;

  // この行の変化点(true = この行で変わった)
  changed: {
    bgm: boolean;
    bg: boolean;
    chara: Record<CharaPosition, boolean>;
  };

  // この行のメッセージ(あれば)
  message: MessageCell | null;

  // 制御マーカー(ラベル開始 / ジャンプ / 選択肢 / マクロ / etc)
  markers: StateMarker[];

  // 選択肢の中身(marker が choice の時に詰める)
  choices?: Array<{ text: string; target: string; conditionRaw?: string }>;

  // 該当する .pix ソース行範囲
  sourceRange: { start: number; end: number };

  // 変数操作のサマリ(@set など)
  variableOps: VariableOp[];
}

export interface VariableOp {
  /** "set" または "unset" */
  kind: "set" | "unset";
  /** 代入式部分の生テキスト (例: "affection += 1") */
  raw: string;
  /** ソース行番号 (1-based) */
  line: number;
}

export interface SceneGrid {
  filename: string;
  rows: SceneRow[];
  /** パース失敗時のメッセージ */
  error: string | null;
}
